/**
 * Context Repository
 */

import { WorkflowContext } from '../types/WorkflowEntities';
import { BaseRepository } from './BaseRepository';

class ContextRepository extends BaseRepository<WorkflowContext> {
    protected entityName = 'context';
    protected graphQLType = 'WorkflowContext';

    /**
     * Find a single context by tenant and ID (FERP Repository Pattern Compliance)
     */
    async findByTenantAndId(tenantId: string, id: string): Promise<WorkflowContext | null> {
        // Log access for audit trail
        await this.auditTrail.logAccess('CONTEXT_ACCESS', {
            tenantId,
            id,
            operation: 'findByTenantAndId',
            timestamp: new Date().toISOString()
        });

        const query = `
            query FindContextByTenantAndId($tenantId: String!, $id: ID!) {
                context(tenant: $tenantId, id: $id) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        const result = await this.executeQuery(query, { tenantId, id }, {
            cache: true,
            cacheTime: 300000
        });
        return result.context || null;
    }

    // Context-specific methods
    async findByInstanceId(instanceId: string): Promise<WorkflowContext[]> {
        const query = `
            query FindContextByInstanceId($instanceId: ID!) {
                contexts(filter: { instanceId: $instanceId }) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        const result = await this.executeQuery(query, { instanceId });
        return result.contexts || [];
    }

    async findByScope(scope: string): Promise<WorkflowContext[]> {
        const query = `
            query FindContextByScope($scope: String!) {
                contexts(filter: { scope: $scope }) {
                    ${this.getFieldSelection()}
                }
            }
        `;

        const result = await this.executeQuery(query, { scope });
        return result.contexts || [];
    }

    async getVariable(contextId: string, variableName: string): Promise<any> {
        const query = `
            query GetContextVariable($contextId: ID!, $variableName: String!) {
                contextVariable(contextId: $contextId, variableName: $variableName) {
                    name
                    value
                    type
                }
            }
        `;

        const result = await this.executeQuery(query, { contextId, variableName });
        return result.contextVariable;
    }

    async setVariable(contextId: string, variableName: string, value: any): Promise<any> {
        const mutation = `
            mutation SetContextVariable($contextId: ID!, $variableName: String!, $value: JSON!) {
                setContextVariable(contextId: $contextId, variableName: $variableName, value: $value) {
                    success
                    version
                }
            }
        `;

        return this.executeQuery(mutation, { contextId, variableName, value });
    }

    async merge(contextId: string, variables: Record<string, any>): Promise<any> {
        const mutation = `
            mutation MergeContextVariables($contextId: ID!, $variables: JSON!) {
                mergeContextVariables(contextId: $contextId, variables: $variables) {
                    success
                    version
                    mergedCount
                }
            }
        `;

        return this.executeQuery(mutation, { contextId, variables });
    }

    protected getFieldSelection(): string {
        return `
            id
            instanceId
            variables
            scope
            readonly
            createdAt
            updatedAt
            version
        `;
    }
}

export const contextRepository = new ContextRepository();
export { ContextRepository };
